import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { ResultType } from './result-type';
import { Conditional, Icon, MedicalCodeChip } from '@codametrix/ui-components';
import { caseUtils, commonEnums } from '@codametrix/ui-common';
import CodeProvenanceChip from '../../code-provenance-chip/code-provenance-chip';
import CodeProvenanceChipRow from '../../code-provenance-chip/code-provenance-chip';

const { ChargeSessionCodeType } = commonEnums;

export type ResultRowProps = {
  activeIdx: number;
  activeSelection: string;
  codeInfo:
    | CMxCommonApp.CodeInfo
    | CMxCommonApp.SuggestedCode
    | CMxCommonApp.SuggestedLinkedProcedure;
  idx: number;
  idxOffset?: number;
  allowCodeRemove?: boolean;
  transactionGuid?: string;
  isSuggestedRow?: boolean;
  isAlreadySelected?: boolean;
  handleClick: (
    event: React.MouseEvent<HTMLDivElement>,
    codeInfo:
      | CMxCommonApp.CodeInfo
      | CMxCommonApp.SuggestedCode
      | CMxCommonApp.SuggestedLinkedProcedure,
    opType: 'add' | 'delete'
  ) => void;
};

const isSuggestedLinkedProcedure = (
  codeInfo:
    | CMxCommonApp.CodeInfo
    | CMxCommonApp.SuggestedCode
    | CMxCommonApp.SuggestedLinkedProcedure
): codeInfo is CMxCommonApp.SuggestedLinkedProcedure => {
  return 'units' in codeInfo && 'modifiers' in codeInfo;
};

export const ResultRow: React.FC<ResultRowProps> = (props: ResultRowProps) => {
  const {
    activeIdx,
    activeSelection,
    codeInfo,
    idx,
    idxOffset = 0,
    allowCodeRemove = false,
    transactionGuid = '',
    isSuggestedRow = false,
    isAlreadySelected = false
  } = props;

  //const index = idxOffset + idx;
  const index = idx;
  const isFromSuggestedResult =
    activeSelection === ResultType.SUGGESTED_RESULTS;
  const [isChecked, setIsChecked] = useState(isAlreadySelected);

  useEffect(() => {
    setIsChecked(isAlreadySelected);
  }, [isAlreadySelected]);

  const activeIndex =
    (isSuggestedRow && activeSelection === ResultType.SUGGESTED_RESULTS) ||
    (!isSuggestedRow && activeSelection === ResultType.SEARCH_RESULTS)
      ? activeIdx
      : -1;

  const styleClasses = [
    'search-result',
    `${codeInfo?.codeType?.toLowerCase()}-code`,
    { 'search-result-submittable': activeIndex === index }
  ];

  const chargeSessionCodeType =
    caseUtils.getCodeTypeFullname(String(codeInfo?.codeType ?? '')) ??
    ChargeSessionCodeType.PROCEDURE;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const checked = !isChecked;
    setIsChecked(checked); // Toggle the state
    const opType = checked ? 'add' : 'delete';
    props.handleClick(event, codeInfo, opType);
  };
  const isLinkedProcedure = isSuggestedLinkedProcedure(codeInfo);

  const modifierList = isLinkedProcedure
    ? codeInfo.modifiers.map((mod) => (
        <MedicalCodeChip
          key={mod.code}
          code={mod.code}
          codeType={ChargeSessionCodeType.MODIFIER}
          editable={false}
        />
      ))
    : '';

  return (
    <div
      key={`${isFromSuggestedResult ? 'suggested' : 'search'}-result-${index}`}
      data-idx={index}
      className={classnames(styleClasses)}
      data-dd-action-name={`~Select code (${codeInfo?.codeType.toLowerCase()}-code)`}
      onClick={(ev) => handleClick(ev)}>
      <Conditional every={[isSuggestedRow, allowCodeRemove]}>
        <Checkbox value={isChecked} onChange={() => {}} />
      </Conditional>
      <Conditional every={[isLinkedProcedure]}>
        <span className="col-units units-chip">{codeInfo.units}</span>
      </Conditional>
      <div className="col-label search-label">
        <MedicalCodeChip
          code={codeInfo?.code}
          codeType={chargeSessionCodeType}
          editable={false}
        />
      </div>
      <Conditional every={[isLinkedProcedure]}>
        <div className="col-modifiers modifier-code">{modifierList}</div>
      </Conditional>
      <div className="col-code-description">{codeInfo?.description}</div>
      <CodeProvenanceChipRow
        codeProvenances={codeInfo?.codeProvenances}
        className="col-code-provenance"
      />
    </div>
  );
};

export type ResultHeaderProps = {
  label: string;
  name: string;
  isSuggestedRow?: boolean;
  allowCodeRemove?: boolean;
  opType?: string;
  handleSelectAll?: (
    event: React.MouseEvent<HTMLDivElement>,
    opType: string
  ) => void;
  handleUnselectAll?: (
    event: React.MouseEvent<HTMLDivElement>,
    opType: string
  ) => void;
  activeIndex?: number;
  sectionStartIndex?: number;
  activeSelection: string;
  fieldDefinitions?: CMxCommonApp.FieldDefinition[];
};

export const ResultHeaderRow: React.FC<ResultHeaderProps> = (
  props: ResultHeaderProps
) => {
  const {
    label,
    isSuggestedRow = false,
    allowCodeRemove = false,
    opType,
    handleSelectAll,
    handleUnselectAll,
    activeIndex,
    sectionStartIndex,
    activeSelection,
    fieldDefinitions
  } = props;

  const styleClasses = classnames('result-header', {
    'suggested-result-header': isSuggestedRow
  });

  const codeType =
    fieldDefinitions && fieldDefinitions?.length > 0
      ? fieldDefinitions[0].key
      : null;

  return (
    <div className={classnames(styleClasses)}>
      <span>{label}</span>
      <Conditional every={[isSuggestedRow, allowCodeRemove, codeType != 'CPT']}>
        <div className="action-container">
          <div
            className={classnames('select-all', {
              highlighted:
                activeSelection === ResultType.SUGGESTED_RESULTS &&
                sectionStartIndex === activeIndex
            })}
            onClick={(ev) => handleSelectAll(ev, opType)}>
            add all
          </div>
          <div
            className={classnames('unselect-all', {
              highlighted:
                activeSelection === ResultType.SUGGESTED_RESULTS &&
                sectionStartIndex + 1 === activeIndex
            })}
            onClick={(ev) => handleUnselectAll(ev, opType)}>
            remove all
          </div>
        </div>
      </Conditional>
    </div>
  );
};

ResultHeaderRow.displayName = 'ResultHeaderRow';

export type CheckboxProps = {
  value?: boolean;
  label?: string;
  disabled?: boolean;
  onChange: (isChecked: boolean) => void;
};

export const Checkbox: React.FC<CheckboxProps> = ({
  value = false,
  label,
  disabled = false,
  onChange = () => {}
}) => {
  const [checked, setChecked] = useState<boolean>(value);

  // Sync state with the `value` prop if it changes
  useEffect(() => {
    if (value !== checked) {
      setChecked(value);
    }
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;

    const newChecked = !checked;
    setChecked(newChecked);
    onChange(newChecked);
  };

  const containerClasses = classnames('cmx-custom-checkbox', {
    checked: checked
  });

  const checkboxClasses = classnames('custom-checkbox', {
    checked: checked,
    'checkbox-disabled': disabled
  });

  return (
    <div className={containerClasses} onClick={handleClick}>
      <div className={checkboxClasses}>
        {checked && (
          <Icon
            svgIconName="BiomeCheckSVG20"
            svgIconSize="12"
            svgIconClass="check-mark"
          />
        )}
      </div>
      {label && <span className="checkbox-label">{label}</span>}
    </div>
  );
};
